/**
 * Favorites button slot placement.
 *
 * The Tangible Favorites plugin injects a <tangible-favorite-button> web
 * component after each hit card via the 'tangible-algolia-hit-template'
 * filter (favorites: assets/src/integrations/algolia.js). It always lands as
 * the LAST sibling of the card markup, i.e. visually below the card.
 *
 * This module makes the placement declarative: when a card template contains
 * an element with the class `tgbl-favorites-slot`, the button appears inside
 * that slot instead. Works with managed, custom and theme card templates —
 * the slot is found purely in the rendered DOM.
 *
 * Mechanics (why mirror instead of move):
 * - The injected button is a preact-managed vnode; moving its DOM node breaks
 *   preact's bookkeeping on the next re-render. So the original stays where
 *   preact put it (hidden via inline style) and a fresh element with the same
 *   attributes is created inside the slot. Web components render themselves
 *   on insertion (connectedCallback -> favorites' bulk-render ajax), so the
 *   mirror needs no wiring beyond its attributes.
 * - Card markup is injected via dangerouslySetInnerHTML, so an InstantSearch
 *   re-render can wipe the slot's contents. syncFavoriteSlots() runs on every
 *   InstantSearch 'render' event and refills any slot that is empty or stale
 *   (hit changed under a position-reused element), making placement
 *   self-healing.
 */

const BUTTON_TAG   = 'tangible-favorite-button';
const SLOT_CLASS   = 'tgbl-favorites-slot';
// Attributes that identify what a button renders (see FavoriteButton in the
// favorites plugin's algolia integration).
const BUTTON_ATTRS = [ 'config-id', 'entity-id', 'entity-type' ];

function sameTarget( a, b ) {
	return BUTTON_ATTRS.every( attr => a.getAttribute( attr ) === b.getAttribute( attr ) );
}

/**
 * Mirrors auto-injected favorite buttons into their card's slot, if it has
 * one. Idempotent: safe (and intended) to call on every render.
 *
 * @param {Element} container The hits widget container element.
 */
export function syncFavoriteSlots( container ) {
	container.querySelectorAll( BUTTON_TAG ).forEach( original => {
		// Only handle the auto-appended buttons, not the mirrors themselves.
		if ( original.closest( `.${ SLOT_CLASS }` ) ) return;

		const item = original.closest( '.ais-Hits-item, .ais-InfiniteHits-item' ) || original.parentElement;
		const slot = item && item.querySelector( `.${ SLOT_CLASS }` );
		if ( ! slot ) return;

		original.style.display = 'none';

		const existing = slot.querySelector( BUTTON_TAG );
		if ( existing && sameTarget( existing, original ) ) return;

		const mirror = document.createElement( BUTTON_TAG );
		BUTTON_ATTRS.forEach( attr => {
			const value = original.getAttribute( attr );
			if ( value !== null ) mirror.setAttribute( attr, value );
		} );
		slot.replaceChildren( mirror );
	} );
}

/**
 * Wires slot syncing to an InstantSearch instance for one widget container.
 * 'render' fires after each DOM commit, i.e. after the favorites filter's
 * buttons exist in the DOM.
 */
export function initFavoritesSlots( search, container ) {
	search.on( 'render', () => syncFavoriteSlots( container ) );
}
